import React, { FC, MouseEvent, useState } from 'react'

import clsx from 'clsx'
import { FieldErrors, UseFieldArrayRemove, UseFieldArrayUpdate } from 'react-hook-form'
import { UseFieldArrayPrepend } from 'react-hook-form/dist/types/fieldArray'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form/dist/types/form'

import { Txt } from 'src/components/atoms'
import { AssignMailCheckbox } from 'src/components/molecules'
import { BulkActionButton } from 'src/components/molecules/inputs/BulkActionButton'
import { UserAssignProjectList, AssigningProjectSearch } from 'src/components/organisms'
import { ChangeCompassRoleModal } from 'src/components/organisms/users/ChangeCompassRoleModal'

import { useGetCompassRoles } from 'src/hooks/useGetCompassRoles'
import { ProjectEntity, UserAssignProjectFormEntity } from 'src/types'

type Props = {
  watch: UseFormWatch<UserAssignProjectFormEntity>
  prepend: UseFieldArrayPrepend<UserAssignProjectFormEntity, 'projects'>
  update: UseFieldArrayUpdate<UserAssignProjectFormEntity, 'projects'>
  setValue: UseFormSetValue<UserAssignProjectFormEntity>
  formErrors: FieldErrors<UserAssignProjectFormEntity>
  remove: UseFieldArrayRemove
  projects: ProjectEntity[]
  className?: string
}

export const UserBulkAssign: FC<Props> = ({
  watch,
  prepend,
  update,
  setValue,
  formErrors,
  remove,
  projects,
  className,
}) => {
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false)

  const { compassRoles, loading } = useGetCompassRoles()

  const indexes = new Map(watch('projects').map(({ projectId }, index) => [projectId, index]))

  const filteredProjects = watch('projects').filter(project => !project.isFiltering)

  const handleOnSelectProject = (project: ProjectEntity) => {
    // 今回のアサイン中に解除したプロジェクトは watch('projects') に存在している
    const index = indexes.get(project.id)
    if (index) {
      // 1度削除した後prependすることで、新規アサインプロジェクトと同じく一番上に表示する
      remove(index)
    }

    prepend({
      projectId: project.id,
      projectName: project.name,
      projectStatusName: project.status.name,
      projectStatusDisplayName: project.status.displayName,
      isAlreadyAssigned: false,
    })
  }

  const selectChangeRole = (event: MouseEvent) => {
    event.preventDefault()
    setShowChangeRoleModal(true)
  }

  const handleOnClickRemove = (event: MouseEvent) => {
    event.preventDefault()

    // 選択しているプロジェクトの削除対象フラグを立てる
    for (const [index, project] of filteredProjects.entries()) {
      if (project.isSelected) {
        update(indexes.get(project.projectId) ?? index, {
          ...project,
          isDeleting: true,
          isSelected: false,
        })
      }
    }
  }

  const changeCompassRole = (updatedCompassRoleId: number) => {
    for (const [index, project] of filteredProjects.entries()) {
      if (project.isSelected) {
        update(indexes.get(project.projectId) ?? index, {
          ...project,
          compassRoleId: updatedCompassRoleId,
          isSelected: false,
        })
      }
    }

    setShowChangeRoleModal(false)
  }

  return (
    <div className={className}>
      <AssignMailCheckbox
        isSendAssignEmail={watch('isSendAssignEmail')}
        checkboxPosition="right"
        updateCheck={updateValue => setValue('isSendAssignEmail', updateValue)}
        className="tw-w-fit tw-ml-auto tw-mt-s3"
      />

      <div
        className={clsx(
          'tw-mt-s2 tw-border-border-secondary tw-border tw-bg-surface-primary ',
          // アサインプロジェクトが0件の場合は上部のみ角丸を付けることでデザインを整える
          watch('projects').some(p => !p.isDeleting)
            ? 'tw-rounded-[var(--s0)]'
            : 'tw-rounded-t-[var(--s0)]'
        )}
      >
        {/* 親コンポーネントでoverflowを用いるとCompass権限用Dropdownも隠れて選択出来ないケースがあるため必要な箇所に角丸を設定 */}
        <div className="tw-bg-surface-secondary tw-p-s-1 tw-rounded-t-[var(--s0)]">
          <Txt sizeType="s" className="tw-font-bold tw-text-standardText-secondary">
            プロジェクト追加
          </Txt>
        </div>
        <AssigningProjectSearch
          alreadyAssignedProjectIds={
            watch('projects')
              // 一括アサインの操作の中で解除を行ったプロジェクトは除外（一覧に表示させる）
              ?.filter(v => !v.isDeleting)
              ?.map(v => v.projectId) ?? []
          }
          onSelectProject={handleOnSelectProject}
        />
        <div>
          {!loading && (
            <UserAssignProjectList
              watch={watch}
              update={update}
              setValue={setValue}
              formErrors={formErrors}
              projects={projects}
              compassRoles={compassRoles}
            />
          )}
        </div>
      </div>

      <BulkActionButton
        isActive={filteredProjects.some(project => project.isSelected)}
        actions={[
          { text: '権限変更', iconPath: 'user-check/soft/line', action: selectChangeRole },
          { text: 'アサイン解除', iconPath: 'trash/soft/line', action: handleOnClickRemove },
        ]}
      />

      {showChangeRoleModal && (
        <ChangeCompassRoleModal
          isOpen={showChangeRoleModal}
          onClose={() => setShowChangeRoleModal(false)}
          changeCompassRole={changeCompassRole}
        />
      )}
    </div>
  )
}
